// Application-level enums to replace database enums

export const USER_ROLES = {
  ADMIN: "admin",
  HR: "hr",
  PANELIST: "panelist",
} as const;

export const JOB_TYPES = {
  PROGRAMMING: "programming",
  NON_PROGRAMMING: "non_programming",
  BUSINESS: "business",
} as const;

export const QUESTION_TYPES = {
  MCQ: "mcq",
  OPEN_ENDED: "open_ended",
} as const;

export const EVALUATION_ROUNDS = {
  CV_PROFILE: "cv_profile",
  MCQ: "mcq",
  OPEN_ENDED: "open_ended",
  VOICE_INTERVIEW: "voice_interview",
  CAMPUS_INTERVIEW: "campus_interview",
  OFFICE_TECHNICAL: "office_technical",
  HR_FOUNDER: "hr_founder",
} as const;

export const CANDIDATE_STATUS = {
  FAILED: "failed",
  PASSED: "passed",
  OFFERED: "offered",
  HOLD: "hold",
  DECLINED: "declined",
  PENDING: "pending",
  SELECTED: "selected",
  ACCEPTED: "accepted",
  JOINED: "joined",
  CV_SUBMITTED: "cv_submitted",
  ASSESSMENT_SENT: "assessment_sent",
  ASSESSMENT_IN_PROGRESS: "assessment_in_progress",
  ASSESSMENT_COMPLETED: "assessment_completed",
} as const;

export const COLLEGE_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

export const BRANCH_TYPES = {
  COMPUTER_SCIENCE: "computer_science",
  INFORMATION_TECHNOLOGY: "information_technology",
  ELECTRONICS_COMMUNICATION: "electronics_communication",
  ELECTRICAL: "electrical",
  MECHANICAL: "mechanical",
  CIVIL: "civil",
  CHEMICAL: "chemical",
  BIOTECHNOLOGY: "biotechnology",
  AEROSPACE: "aerospace",
  AUTOMOBILE: "automobile",
  MARINE: "marine",
  TEXTILE: "textile",
  METALLURGY: "metallurgy",
  MINING: "mining",
  PETROLEUM: "petroleum",
  PRODUCTION: "production",
  INDUSTRIAL: "industrial",
  INSTRUMENTATION: "instrumentation",
  BIOMEDICAL: "biomedical",
  AGRICULTURAL: "agricultural",
  FOOD_TECHNOLOGY: "food_technology",
  OTHER: "other",
} as const;

export const QUALIFICATION_TYPES = {
  BTECH: "btech",
  BE: "be",
  BSC: "bsc",
  MTECH: "mtech",
  ME: "me",
  MSC: "msc",
  PHD: "phd",
  DIPLOMA: "diploma",
  BCA: "bca",
  MCA: "mca",
  BBA: "bba",
  MBA: "mba",
  OTHER: "other",
} as const;

// Branches offered to candidates (registration + candidate filter share this list)
export const BRANCH_OPTIONS = [
  BRANCH_TYPES.COMPUTER_SCIENCE,
  BRANCH_TYPES.INFORMATION_TECHNOLOGY,
  BRANCH_TYPES.ELECTRONICS_COMMUNICATION,
  BRANCH_TYPES.ELECTRICAL,
  BRANCH_TYPES.MECHANICAL,
  BRANCH_TYPES.CIVIL,
  BRANCH_TYPES.CHEMICAL,
  BRANCH_TYPES.BIOTECHNOLOGY,
  BRANCH_TYPES.OTHER,
] as const;

// Qualifications offered to candidates (registration + candidate filter share this list)
export const QUALIFICATION_OPTIONS = [
  QUALIFICATION_TYPES.BTECH,
  QUALIFICATION_TYPES.BE,
  QUALIFICATION_TYPES.BSC,
  QUALIFICATION_TYPES.MTECH,
  QUALIFICATION_TYPES.ME,
  QUALIFICATION_TYPES.MSC,
  QUALIFICATION_TYPES.PHD,
  QUALIFICATION_TYPES.DIPLOMA,
  QUALIFICATION_TYPES.BCA,
  QUALIFICATION_TYPES.MCA,
  QUALIFICATION_TYPES.BBA,
  QUALIFICATION_TYPES.MBA,
  QUALIFICATION_TYPES.OTHER,
] as const;

export const DEFAULT_SKILLS = [
  "JavaScript",
  "TypeScript",
  "React",
  "Node.js",
  "Python",
  "Java",
  "C++",
  "SQL",
  "MongoDB",
  "PostgreSQL",
  "Git",
  "Docker",
  "AWS",
  "Azure",
  "Machine Learning",
  "Data Analysis",
  "UI/UX Design",
  "Project Management",
  "Communication",
  "Leadership",
] as const;

export const DEFAULT_TECHNOLOGIES = [
  "React",
  "Angular",
  "Vue.js",
  "Node.js",
  "Express.js",
  "Django",
  "Flask",
  "Spring Boot",
  "Laravel",
  "MongoDB",
  "PostgreSQL",
  "MySQL",
  "Redis",
  "Docker",
  "Kubernetes",
  "AWS",
  "Azure",
  "GCP",
  "Jenkins",
  "GitHub Actions",
] as const;

export const DEFAULT_HOBBIES = [
  "Reading",
  "Writing",
  "Photography",
  "Traveling",
  "Cooking",
  "Sports",
  "Music",
  "Gaming",
  "Hiking",
  "Cycling",
  "Swimming",
  "Dancing",
  "Volunteering",
  "Art",
  "Coding",
  "Learning Languages",
] as const;

// Type definitions
export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];
export type JobType = (typeof JOB_TYPES)[keyof typeof JOB_TYPES];
export type QuestionType = (typeof QUESTION_TYPES)[keyof typeof QUESTION_TYPES];
export type EvaluationRound =
  (typeof EVALUATION_ROUNDS)[keyof typeof EVALUATION_ROUNDS];
export type CandidateStatus =
  (typeof CANDIDATE_STATUS)[keyof typeof CANDIDATE_STATUS];
export type CollegeStatus =
  (typeof COLLEGE_STATUS)[keyof typeof COLLEGE_STATUS];
export type BranchType = (typeof BRANCH_TYPES)[keyof typeof BRANCH_TYPES];
export type QualificationType =
  (typeof QUALIFICATION_TYPES)[keyof typeof QUALIFICATION_TYPES];

// Helper functions to get display labels
export const getUserRoleLabel = (role: UserRole): string => {
  const labels = {
    [USER_ROLES.ADMIN]: "Admin",
    [USER_ROLES.HR]: "HR",
    [USER_ROLES.PANELIST]: "Panelist",
  };
  return labels[role] || role;
};

export const getJobTypeLabel = (jobType: JobType): string => {
  const labels = {
    [JOB_TYPES.PROGRAMMING]: "Programming",
    [JOB_TYPES.NON_PROGRAMMING]: "Non Programming",
    [JOB_TYPES.BUSINESS]: "Business",
  };
  return labels[jobType] || jobType;
};

export const getQuestionTypeLabel = (questionType: QuestionType): string => {
  const labels = {
    [QUESTION_TYPES.MCQ]: "Multiple Choice",
    [QUESTION_TYPES.OPEN_ENDED]: "Open Ended",
  };
  return labels[questionType] || questionType;
};

export const getEvaluationRoundLabel = (round: EvaluationRound): string => {
  const labels = {
    [EVALUATION_ROUNDS.CV_PROFILE]: "CV/Profile",
    [EVALUATION_ROUNDS.MCQ]: "MCQ",
    [EVALUATION_ROUNDS.OPEN_ENDED]: "Open Ended",
    [EVALUATION_ROUNDS.VOICE_INTERVIEW]: "Voice Interview",
    [EVALUATION_ROUNDS.CAMPUS_INTERVIEW]: "1:1 in Campus",
    [EVALUATION_ROUNDS.OFFICE_TECHNICAL]: "1:1 in Office Technical",
    [EVALUATION_ROUNDS.HR_FOUNDER]: "HR/Founder",
  };
  return labels[round] || round;
};

export const getCandidateStatusLabel = (status: CandidateStatus): string => {
  const labels = {
    [CANDIDATE_STATUS.FAILED]: "Failed",
    [CANDIDATE_STATUS.PASSED]: "Passed",
    [CANDIDATE_STATUS.OFFERED]: "Offered",
    [CANDIDATE_STATUS.HOLD]: "On Hold",
    [CANDIDATE_STATUS.DECLINED]: "Declined",
    [CANDIDATE_STATUS.PENDING]: "Pending",
    [CANDIDATE_STATUS.SELECTED]: "Selected",
    [CANDIDATE_STATUS.ACCEPTED]: "Accepted",
    [CANDIDATE_STATUS.JOINED]: "Joined",
    [CANDIDATE_STATUS.CV_SUBMITTED]: "CV Submitted",
    [CANDIDATE_STATUS.ASSESSMENT_SENT]: "Assessment Sent",
    [CANDIDATE_STATUS.ASSESSMENT_IN_PROGRESS]: "Assessment In Progress",
    [CANDIDATE_STATUS.ASSESSMENT_COMPLETED]: "Assessment Completed",
  };
  return labels[status] || status;
};

export const getCollegeStatusLabel = (status: CollegeStatus): string => {
  const labels = {
    [COLLEGE_STATUS.ACTIVE]: "Active",
    [COLLEGE_STATUS.INACTIVE]: "Inactive",
  };
  return labels[status] || status;
};

export const getBranchTypeLabel = (branch: BranchType): string => {
  const labels = {
    [BRANCH_TYPES.COMPUTER_SCIENCE]: "Computer Science",
    [BRANCH_TYPES.INFORMATION_TECHNOLOGY]: "Information Technology",
    [BRANCH_TYPES.ELECTRONICS_COMMUNICATION]: "Electronics & Communication",
    [BRANCH_TYPES.ELECTRICAL]: "Electrical",
    [BRANCH_TYPES.MECHANICAL]: "Mechanical",
    [BRANCH_TYPES.CIVIL]: "Civil",
    [BRANCH_TYPES.CHEMICAL]: "Chemical",
    [BRANCH_TYPES.BIOTECHNOLOGY]: "Biotechnology",
    [BRANCH_TYPES.AEROSPACE]: "Aerospace",
    [BRANCH_TYPES.AUTOMOBILE]: "Automobile",
    [BRANCH_TYPES.MARINE]: "Marine",
    [BRANCH_TYPES.TEXTILE]: "Textile",
    [BRANCH_TYPES.METALLURGY]: "Metallurgy",
    [BRANCH_TYPES.MINING]: "Mining",
    [BRANCH_TYPES.PETROLEUM]: "Petroleum",
    [BRANCH_TYPES.PRODUCTION]: "Production",
    [BRANCH_TYPES.INDUSTRIAL]: "Industrial",
    [BRANCH_TYPES.INSTRUMENTATION]: "Instrumentation",
    [BRANCH_TYPES.BIOMEDICAL]: "Biomedical",
    [BRANCH_TYPES.AGRICULTURAL]: "Agricultural",
    [BRANCH_TYPES.FOOD_TECHNOLOGY]: "Food Technology",
    [BRANCH_TYPES.OTHER]: "Other",
  };
  return labels[branch] || branch;
};

export const getQualificationTypeLabel = (
  qualification: QualificationType
): string => {
  const labels = {
    [QUALIFICATION_TYPES.BTECH]: "B.Tech",
    [QUALIFICATION_TYPES.BE]: "B.E.",
    [QUALIFICATION_TYPES.BSC]: "B.Sc",
    [QUALIFICATION_TYPES.MTECH]: "M.Tech",
    [QUALIFICATION_TYPES.ME]: "M.E.",
    [QUALIFICATION_TYPES.MSC]: "M.Sc",
    [QUALIFICATION_TYPES.PHD]: "Ph.D",
    [QUALIFICATION_TYPES.DIPLOMA]: "Diploma",
    [QUALIFICATION_TYPES.BCA]: "BCA",
    [QUALIFICATION_TYPES.MCA]: "MCA",
    [QUALIFICATION_TYPES.BBA]: "BBA",
    [QUALIFICATION_TYPES.MBA]: "MBA",
    [QUALIFICATION_TYPES.OTHER]: "Other",
  };
  return labels[qualification] || qualification;
};
